import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { useMetrics } from './useMetrics';

const useCreator = (creatorId?: string) => {
  const client = useQueryClient();
  const metrics = useMetrics();

  const creatorCacheKey = ['creators', creatorId];
  const creatorFollowerCacheKey = ['followingcreator', creatorId];

  const { data: creator, isFetching: isFetchingCreator } = useQuery(
    creatorCacheKey,
    async () => {
      if (!creatorId) return Promise.resolve(undefined);
      return BackendService.instance.discover.getCreator(creatorId);
    },
    {
      staleTime: 1000 * 60 * 5,
      retry: false,
    },
  );
  const { mutate: followCreator, isLoading: isLoadingFollowing } = useMutation(
    ({ creatorId, activeTenantId }: { creatorId: string; activeTenantId: string }) => {
      metrics.logEvent('Creator.Follow', { creatorId });
      return BackendService.instance.discover.followCreator(creatorId, activeTenantId);
    },
    {
      onMutate: async () => {
        const creatorFollowerCacheKey = ['followingcreator', creatorId];
        client.cancelQueries(creatorFollowerCacheKey);
        const previousCache = client.getQueryData<boolean | undefined>(
          creatorFollowerCacheKey,
        );
        client.setQueryData<boolean | undefined>(creatorFollowerCacheKey, () => {
          return true;
        });
        // Return the snapshotted value
        return () => client.setQueryData(creatorFollowerCacheKey, previousCache);
      },
      onError: (_, vars, rollback) => {
        if (rollback) {
          rollback();
        }
      },
      onSettled: () => {
        client.invalidateQueries(creatorFollowerCacheKey);
        client.invalidateQueries(creatorCacheKey);
      },
    },
  );

  const { mutate: unfollowCreator, isLoading: isLoadingUnFollowing } = useMutation(
    ({ creatorId, activeTenantId }: { creatorId: string; activeTenantId: string }) => {
      metrics.logEvent('Creator.Unfollow', { creatorId });
      return BackendService.instance.discover.unfollowCreator(creatorId, activeTenantId);
    },
    {
      onMutate: async () => {
        client.cancelQueries(creatorFollowerCacheKey);
        const previousCache = client.getQueryData<boolean | undefined>(
          creatorFollowerCacheKey,
        );
        client.setQueryData<boolean | undefined>(creatorFollowerCacheKey, () => {
          return false;
        });
        // Return the snapshotted value
        return () => client.setQueryData(creatorFollowerCacheKey, previousCache);
      },
      onError: (_, vars, rollback) => {
        if (rollback) {
          rollback();
        }
      },
      onSettled: () => {
        client.invalidateQueries(creatorFollowerCacheKey);
        client.invalidateQueries(creatorCacheKey);
      },
    },
  );

  return {
    creator,
    followCreator,
    isFetchingCreator,
    isLoadingFollowing,
    isLoadingUnFollowing,
    unfollowCreator,
    firstLetter: creator?.nickName?.substring(0, 1) || '-',
  };
};

export default useCreator;
